import api from 'api/api'
import { accessTokens } from 'api/accessTokenMgmt'

export interface MemeAutocompleteItem {
    id: string
    text: string
    thread_count: number
}

// One global label list shared by every editor instance; the server caps it at
// its top-N hub memes and the client filters per keystroke (mention-style), so
// typing in the dropdown never hits the network.
const CACHE_TTL_MS = 5 * 60 * 1000

let cachedItems: MemeAutocompleteItem[] = []
let cachedAt = 0
let inflight: Promise<MemeAutocompleteItem[]> | null = null

async function fetchGlobalMemes(): Promise<MemeAutocompleteItem[]> {
    const response = await fetch(`${window.knovApiUrl}/api/v1/memes/autocomplete`, {
        headers: {
            ...api.commonHeaders(),
            ...accessTokens(),
            'X-CSRF-Token': gon.formToken,
        },
        credentials: 'same-origin',
    })

    if (!response.ok) {
        throw new Error('Failed to fetch meme suggestions')
    }

    const data = await response.json()
    return data.memes || []
}

export async function getGlobalMemes(): Promise<MemeAutocompleteItem[]> {
    if (cachedItems.length && Date.now() - cachedAt < CACHE_TTL_MS) {
        return cachedItems
    }
    if (!inflight) {
        inflight = fetchGlobalMemes()
            .then(items => {
                cachedItems = items
                cachedAt = Date.now()
                return items
            })
            .catch(() => cachedItems) // Keep stale items on failure
            .finally(() => {
                inflight = null
            })
    }
    return inflight
}

// Prefix matches first, then word-boundary, then substring; ties broken by
// thread count (the server list arrives count-ranked, sort is stable).
export function rankMemeMatches(
    items: MemeAutocompleteItem[],
    query: string,
    limit = 8,
): MemeAutocompleteItem[] {
    const normalized = query.trim().toLowerCase()
    if (!normalized) return items.slice(0, limit)

    const rank = (item: MemeAutocompleteItem): number => {
        const text = item.text.toLowerCase()
        if (text.startsWith(normalized)) return 0
        if (text.includes(` ${normalized}`)) return 1
        if (text.includes(normalized)) return 2
        return Number.POSITIVE_INFINITY
    }

    return items
        .map(item => ({ item, rank: rank(item) }))
        .filter(entry => entry.rank !== Number.POSITIVE_INFINITY)
        .sort((a, b) => a.rank - b.rank)
        .slice(0, limit)
        .map(entry => entry.item)
}
